"use client";

import { cn } from "@/lib/utils";
import { usePageCatalog } from "@app/_components/PageCatalog/PageCatalogReader";
import useTranslate from "@app/_hooks/useTranslate";
import type { TextFacetValue } from "@apptus/esales-api";
import fuzzysort from "fuzzysort";
import { type ComponentProps, useState } from "react";
import VirtualizedList from "./VirtualizedList";

export default function TextFilter({
  identifier,
  facet,
  className,
  ...props
}: {
  identifier: string;
  facet: { id: string; values: TextFacetValue[] };
} & ComponentProps<"section">) {
  const t = useTranslate();
  const pageCatalog = usePageCatalog();
  const [search, setSearch] = useState("");
  const searchLabel = t("searchTerm");

  const values = facet.values
    .map((value) => {
      const id = value.id.replace(identifier, "");
      const translation = t(id, { strict: true });
      if (translation) {
        return { ...value, label: translation };
      }
      const pageCatalogEntry = pageCatalog?.get(id);
      if (pageCatalogEntry) {
        return { ...value, label: pageCatalogEntry.title ?? value.label };
      }
      // Skip values that contain underscores, these are likely codes
      if (id.includes("_")) {
        return undefined;
      }
      return value;
    })
    .filter((value) => value !== undefined);

  return (
    <section key={facet.id} className={cn(className)} {...props}>
      {values.length > 10 && (
        <input
          name="search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={searchLabel}
          placeholder={searchLabel}
          className="mb-2 w-full border border-black px-2 py-1"
        />
      )}
      <VirtualizedList
        name={facet.id}
        values={fuzzysort
          .go(search, values, { key: "label", limit: 100 })
          .map((res) => res.obj)}
      />
    </section>
  );
}
